import React, { useMemo, useState } from 'react';
import { gql, useQuery, useNavigate } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Button, Modal, Row, Typography } from 'antd';
import {
  Banner,
  BaseButton,
  Card,
  EmptyContent,
  ErrorHandler,
  Gutter,
  Icon,
  LoadingState,
} from '@common/components';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  ComplianceItemApprovalList,
  ComplianceItemApprovalListEdition,
} from '@onboarding/components';

const { Paragraph, Text } = Typography;

const CAD_COMPLIANCE_TAB_QUERY = gql`
  query CandidateAssignmentQueryComplianceTabQuery($id: ID!) {
    worksList(id: $id) {
      id
      ...WorksListWorksItemApprovalListInfo
    }
  }
  ${ComplianceItemApprovalList.fragments.worksList}
`;

type Props = {
  bannerSlot: React.ReactNode;
  worksListId: string;
};

export function CandidateAssignmentComplianceTab({
  worksListId,
  bannerSlot,
}: Props) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);

  const { loading, error, data, refetch } = useQuery(CAD_COMPLIANCE_TAB_QUERY, {
    variables: {
      id: worksListId,
    },
  });

  const currentMembership = useCurrentMembership();

  const hasComplianceTemplate = useMemo(
    () => data?.worksList?.worksListTemplate !== null,
    [data]
  );

  const canEditComplianceList = useMemo(
    () => currentMembership.canEditIndividualComplianceList,
    [currentMembership]
  );

  const ongoingAssigment = useMemo(() => {
    const state = data?.worksList?.staffingRequestAssignment?.state;
    return state !== 'cancelled' && state !== 'completed';
  }, [data]);

  const worksListTemplateId = useMemo(
    () => data?.worksList?.worksListTemplate?.id,
    [data]
  );

  const onEditTemplate = () => {
    if (worksListTemplateId) {
      Modal.destroyAll();
      navigate(
        `/works/admin/templates/compliance-list/${worksListTemplateId}/items`
      );
    }
  };

  const handleRequestEdit = () => {
    // Check if we still have a linked compliance template
    if (worksListTemplateId) {
      setShowComplianceModal(true);
    } else {
      setIsEditing(true);
    }
  };

  if (error) {
    return (
      <ErrorHandler error={error} log="CandidateAssignmentComplianceTab" />
    );
  }

  if (loading && !data) return <LoadingState />;

  if (!data?.worksList?.worksListItems?.length && !loading) {
    return (
      <EmptyContent
        height={300}
        imagePath="./onboarding/empty-team.svg"
        text="No compliance items for this assignment."
      />
    );
  }

  return (
    <section className="CandidateAssignmentComplianceTab">
      <Modal
        title={
          <>
            <Gutter direction="right" inlineBlock>
              <Icon
                icon="exclamationTriangle"
                kind="solid"
                size="lg"
                color="harvestGold"
              />
            </Gutter>
            Choose what you want to do:
          </>
        }
        onCancel={() => setShowComplianceModal(false)}
        className="compliance-list-update"
        open={showComplianceModal}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setIsEditing(true);
                setShowComplianceModal(false);
              }}
            >
              Edit this List
            </Button>
            <Button type="primary" onClick={onEditTemplate}>
              Edit the Template
            </Button>
          </div>
        }
      >
        <a onClick={onEditTemplate}>
          <Text strong>1. Edit the Template</Text>{' '}
          <Icon
            icon="arrowUpRightFromSquare"
            kind="light"
            size="md"
            color="elm"
          />
        </a>
        <Paragraph type="secondary">
          This and all other assignments sharing the compliance template will be
          updated.
        </Paragraph>
        <Text strong>2. Edit this Compliance List only</Text>{' '}
        <Text type="secondary">(not recommended)</Text>
        <Paragraph type="secondary">
          Only this assignment&apos;s list is updated, and it will not get any
          future updates from the template.
        </Paragraph>
      </Modal>
      <Card>
        <div style={{ marginBottom: 16 }}>{bannerSlot}</div>
        {canEditComplianceList && !hasComplianceTemplate && (
          <div style={{ marginBottom: 16 }}>
            <Banner type="info" closable>
              This assignment&apos;s compliance list is not linked to a
              compliance template and therefore does not get any template
              updates.
            </Banner>
          </div>
        )}
        {isEditing ? (
          <ComplianceItemApprovalListEdition
            worksList={data.worksList}
            onCancel={() => setIsEditing(false)}
            onSave={() => {
              refetch();
              setIsEditing(false);
            }}
            hasComplianceTemplate={hasComplianceTemplate}
          />
        ) : (
          <ComplianceItemApprovalList worksList={data.worksList} />
        )}
        {canEditComplianceList && ongoingAssigment && !isEditing && (
          <Row justify="end" style={{ padding: '8px 16px', marginTop: '8px' }}>
            <BaseButton
              icon={<Icon icon="pencil" size="sm" />}
              onClick={handleRequestEdit}
            >
              Edit List
            </BaseButton>
          </Row>
        )}
      </Card>

      <style jsx>{`
        .CandidateAssignmentComplianceTab {
          margin-top: ${Spacing.baseSpacing / 2}px;
        }
        .compliance-list-update .ant-modal-content {
          width: 500px;
        }
        .compliance-list-update .ant-modal-confirm-body {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        .compliance-list-update
          .ant-modal-confirm-body
          .ant-modal-confirm-title {
          font-weight: bold;
        }
      `}</style>
    </section>
  );
}
